import { useTranslation } from 'react-i18next';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import AiDetectingPlaceholder from 'routes/Documentation/components/AiDetection/AiDetectingPlaceholder';
import CreditsFooter from './CreditsFooter';
import styles from './AiFieldAssist.module.scss';

type Props = {
  isGenerating: boolean;
  isGenerated: boolean;
  // Idle-state label (non-interactive) / post-generation "Completed" replaces it.
  idleLabel: string;
  // Idle CTA / Regenerate CTA button text.
  ctaLabel: string;
  // Placeholder copy while a live generation runs.
  detectingLabel: string;
  onGenerate: () => void;
  onCancel: () => void;
  creditsRemaining?: AiCredits | null;
  // Set while the company's AI budget refuses spend: the CTA is disabled and
  // the footer says why, instead of letting the user start a generation the
  // server would reject.
  blockedText?: string | null;
};

// ── Idle / live generating / generated ──
export default function MainContent({
  isGenerating,
  isGenerated,
  idleLabel,
  ctaLabel,
  detectingLabel,
  onGenerate,
  onCancel,
  creditsRemaining,
  blockedText,
}: Props) {
  const { t } = useTranslation();
  return (
    <div className={styles.popoverBody}>
      <div className={styles.popoverHeader}>
        {isGenerating ? (
          <>
            <AiDetectingPlaceholder label={detectingLabel} />
            <div className={styles.popoverActions}>
              <Button
                buttonText={t('cancel')}
                buttonType={ButtonTypes.secondaryOutlined}
                buttonSize={ButtonSizes.small}
                leftIcon="stop_circle"
                onClick={onCancel}
              />
            </div>
          </>
        ) : (
          <>
            <div className={styles.popoverMessage}>
              <MaterialIcon
                icon={isGenerated ? 'check_circle' : 'auto_awesome'}
                symbolsOutlined
                size="md-20"
              />
              <Typography variant="body">
                {isGenerated ? t('completed') : idleLabel}
              </Typography>
            </div>
            <div className={styles.popoverActions}>
              <Button
                buttonText={ctaLabel}
                buttonType={
                  isGenerated
                    ? ButtonTypes.secondaryOutlined
                    : ButtonTypes.primaryFilled
                }
                buttonSize={ButtonSizes.small}
                disabled={!!blockedText}
                onClick={onGenerate}
              />
            </div>
          </>
        )}
      </div>
      <CreditsFooter credits={creditsRemaining} blockedText={blockedText} />
    </div>
  );
}
